// src/components/blog/features/HomeTopicsGrid.tsx

"use client"

import { motion } from 'motion/react'
import type { Topic } from '~/lib/blog/types'
import Link from 'next/link'
import { cn } from '~/lib/utils'
import { IconComponent } from '~/components/shared/IconComponent'
import { getIconComponent } from '~/lib/blog/utils/icon-map'

interface HomeTopicsGridProps {
  topics: Topic[]
  className?: string
}

export function HomeTopicsGrid({ topics, className }: HomeTopicsGridProps): JSX.Element {
  return (
    <div className={cn("flex h-full flex-col py-4", className)}>
      <div className="mb-8 flex items-center justify-between">
        <h2 className="text-2xl font-bold tracking-tight text-white">
          Featured Topics
        </h2>
        <Link
          href="/blog/topics"
          className="text-sm font-medium text-blue-400 hover:text-blue-300"
        >
          View all topics
          <IconComponent name="RiArrowRightLine" className="ml-1 inline-block h-4 w-4" />
        </Link>
      </div>

      <div className="grid flex-1 gap-6">
        {topics.map((topic, index) => {
          const IconComponent = getIconComponent(topic.iconName)
          return (
            <motion.div
              key={topic.slug}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <Link
                href={topic.href}
                className="group flex items-start gap-6 rounded-xl bg-gray-800/50 p-6 transition-colors hover:bg-gray-800"
              >
                <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-lg bg-blue-500/10">
                  <IconComponent className="h-8 w-8 text-blue-400 transition-colors group-hover:text-blue-300" />
                </div>
                <div className="flex-1 pt-1">
                  <h3 className="text-lg font-medium text-white group-hover:text-blue-400">
                    {topic.title}
                  </h3>
                  {topic.description && (
                    <p className="mt-2 text-sm leading-relaxed text-gray-400 line-clamp-3">
                      {topic.description}
                    </p>
                  )}
                </div>
              </Link>
            </motion.div>
          )}
        )}
      </div>
    </div>
  )
}